import React, { createContext, useContext, useMemo, useState } from 'react';
import { Icon } from './AppShell';

const SESSION_KEY = 'shd-admin-session';
const ADMIN_LOGIN = process.env.REACT_APP_ADMIN_LOGIN || 'admin';
const ADMIN_PASSWORD_HASH = process.env.REACT_APP_ADMIN_PASSWORD_HASH || '';

const hex = (buffer) =>
  [...new Uint8Array(buffer)].map((byte) => byte.toString(16).padStart(2, '0')).join('');

const sha256 = async (value) => {
  const encoded = new TextEncoder().encode(value);
  const digest = await window.crypto.subtle.digest('SHA-256', encoded);
  return hex(digest);
};

const readSession = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return false;
    if (raw === 'true') return true;

    const session = JSON.parse(raw);
    return Boolean(session?.authenticated);
  } catch {
    localStorage.removeItem(SESSION_KEY);
    return false;
  }
};

const writeSession = () => {
  localStorage.setItem(SESSION_KEY, JSON.stringify({ authenticated: true }));
};

const AuthContext = createContext({ logout: () => undefined });

export const useAuth = () => useContext(AuthContext);

const AuthGate = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(readSession);
  const [login, setLogin] = useState(ADMIN_LOGIN);
  const [password, setPassword] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isConfigured = useMemo(() => Boolean(ADMIN_PASSWORD_HASH), []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!isConfigured) {
      setError('Пароль администратора не настроен.');
      return;
    }

    setIsSubmitting(true);
    try {
      const passwordHash = await sha256(password);
      if (login.trim() === ADMIN_LOGIN && passwordHash === ADMIN_PASSWORD_HASH) {
        writeSession();
        setIsAuthenticated(true);
        setPassword('');
      } else {
        setError('Неверный логин или пароль.');
      }
    } catch {
      setError('Браузер не поддерживает безопасную проверку пароля.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(SESSION_KEY);
    setIsAuthenticated(false);
  };

  if (isAuthenticated) {
    return (
      <AuthContext.Provider value={{ logout: handleLogout }}>
        {children}
      </AuthContext.Provider>
    );
  }

  return (
    <div className="login-screen">
      <form onSubmit={handleSubmit} className="login-card">
        <div className="brand-lockup">
          <div className="brand-mark">
            <Icon name="outdoor_grill" />
          </div>
          <h1>Шашлычный дом</h1>
          <strong>Админ панель</strong>
          <p>Меню. Заказы. Контактная информация.</p>
        </div>

        <label className="auth-field">
          <Icon name="person_outline" />
          <input
            value={login}
            onChange={(event) => setLogin(event.target.value)}
            placeholder="Логин"
            autoComplete="username"
          />
        </label>

        <label className="auth-field">
          <Icon name="lock_outline" />
          <input
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Пароль"
            type={isPasswordVisible ? 'text' : 'password'}
            autoComplete="current-password"
          />
          <button
            type="button"
            className="field-icon-button"
            onClick={() => setIsPasswordVisible((value) => !value)}
            aria-label={isPasswordVisible ? 'Скрыть пароль' : 'Показать пароль'}
          >
            <Icon name={isPasswordVisible ? 'visibility_off' : 'visibility'} />
          </button>
        </label>

        {error ? <div className="form-error">{error}</div> : null}

        <button type="submit" disabled={isSubmitting} className="primary-action">
          {isSubmitting ? 'Проверка...' : 'Войти'}
        </button>
      </form>
    </div>
  );
};

export default AuthGate;
